import { FC } from 'react';
import { Box, Button } from '@mui/material';
import theme from 'App.theme';
import { ActionItemsTotals, ActionItemType } from '../types';
import useActionItems from '../hooks/useActionItems';
import { useSelectedActionItem } from '../hooks/useSelectedActionItem';

type ActionItemsMenuProps = {
  recordTotals: ActionItemsTotals;
};

const formatTotal = (total: number): string => (total == 1000 ? '1K' : total > 1000 ? '1K+' : total.toString());

const ActionItemsMenu: FC<ActionItemsMenuProps> = ({ recordTotals }) => {
  const [selectedActionItem, setSelectedActionItem] = useSelectedActionItem();
  const actionItems = useActionItems();

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.layout.spacing.medium,
        mb: theme.layout.spacing.medium,
      }}
    >
      {Object.entries(actionItems).map(([actionItemType, actionItem]) => {
        const isSelected = selectedActionItem == actionItemType;
        const fgColor = actionItem.colors.foreground;
        const bgColor = actionItem.colors.background;
        const recordTotal = recordTotals[actionItemType as ActionItemType];

        return (
          <Box
            key={actionItemType}
            sx={{
              position: 'relative',
              pt: 1,
              pb: 1,
              cursor: 'pointer',
              '&:hover': {
                '& button': {
                  fontWeight: 'bold',
                  // override the default btn hover
                  border: `2px solid ${fgColor}`,
                  backgroundColor: isSelected ? fgColor : theme.colors.white,
                },
              },
            }}
            onClick={() => setSelectedActionItem(actionItemType as ActionItemType)}
          >
            <Button
              variant="outlined"
              sx={{
                minWidth: 240,
                height: 42,
                borderRadius: 50,
                border: `2px solid ${fgColor}`,
                backgroundColor: isSelected ? fgColor : theme.colors.white,
                color: isSelected ? theme.colors.white : fgColor,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                fontWeight: isSelected ? 'bold' : 'normal',
              }}
            >
              {actionItem.lang.label}
            </Button>

            {recordTotal.total > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -5,
                  right: -1,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: fgColor,
                  color: bgColor,
                  border: `1px solid ${bgColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  zIndex: 1,
                }}
              >
                {formatTotal(recordTotal.total)}
              </Box>
            )}
            {recordTotal.expressCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -5,
                  right: 30,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.white,
                  color: theme.colors.red,
                  border: `1px solid ${theme.colors.red}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  zIndex: 1,
                }}
              >
                {formatTotal(recordTotal.expressCount)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ActionItemsMenu;
